/**
 * 运营指标（平台运营端）
 *
 * 指标页的价值在于「一眼看出哪里不对劲」，所以除了数字本身，
 * 每个指标都配了判定口径（多少算正常、多少要联系技术实施组），
 * 避免运营人员看到一个 94% 不知道是好还是坏。
 *
 * 数据来源：GET /api/platform/metrics。接口未就绪时展示演示数据并明确标注。
 */

import { useMemo } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Progress,
  Row,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  ApiOutlined,
  CloudServerOutlined,
  FileTextOutlined,
  ReloadOutlined,
  RiseOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { PROVIDER_LABELS, type PlatformMetrics, type ProviderStat } from '@ilc/shared';
import { platformApi } from '@/api/endpoints';
import { mockPlatformMetricData } from '@/mocks';
import { useResource } from '@/hooks/useResource';
import { PageHeader } from '@/components/PageHeader';
import { PageState, FallbackNotice } from '@/components/PageState';

/** 成功率健康阈值：低于 warn 需要关注，低于 bad 建议立即排查 */
const SUCCESS_RATE_WARN = 0.97;
const SUCCESS_RATE_BAD = 0.9;
/** 平均耗时阈值（毫秒） */
const DURATION_WARN = 2000;
const DURATION_BAD = 3000;

function rateColor(rate: number): string {
  if (rate < SUCCESS_RATE_BAD) return '#cf1322';
  if (rate < SUCCESS_RATE_WARN) return '#d46b08';
  return '#389e0d';
}

function durationColor(ms: number): string | undefined {
  if (ms >= DURATION_BAD) return '#cf1322';
  if (ms >= DURATION_WARN) return '#d46b08';
  return undefined;
}

export default function Metrics(): React.ReactElement {
  const metrics = useResource<PlatformMetrics>(() => platformApi.metrics(), {
    fallback: () => mockPlatformMetricData(),
  });

  const data = metrics.data;

  const stats = useMemo(() => {
    const providerStats = data?.providerStats ?? [];
    const totalCalls = providerStats.reduce((sum, p) => sum + p.totalCalls, 0);
    const totalCost = providerStats.reduce((sum, p) => sum + p.totalCostCents, 0);
    const abnormal = providerStats.filter(
      (p) => p.successRate < SUCCESS_RATE_WARN || p.avgDurationMs >= DURATION_WARN,
    );
    return { totalCalls, totalCost, abnormal };
  }, [data]);

  return (
    <div className="ilc-page">
      <PageHeader
        title="运营指标"
        description="平台维度的租户规模、发票处理量与外部接口健康度。用于发现需要介入的租户与需要排查的接口。"
        extra={
          <Button icon={<ReloadOutlined />} onClick={metrics.reload}>
            刷新
          </Button>
        }
      />

      {metrics.isFallback ? (
        <FallbackNotice
          reason={metrics.fallbackReason}
          onRetry={metrics.reload}
          detail="平台运营指标接口（GET /api/platform/metrics）正在并行开发中，当前展示的是本地演示数据，不是真实运营数据。"
        />
      ) : null}

      {stats.abnormal.length > 0 ? (
        <Alert
          type="warning"
          showIcon
          icon={<WarningOutlined />}
          style={{ marginBottom: 12 }}
          message={`有 ${stats.abnormal.length} 个接口的成功率或耗时未达健康线`}
          description={
            <Space direction="vertical" size={2}>
              <span>
                成功率低于 {Math.round(SUCCESS_RATE_WARN * 100)}% 或平均耗时高于 {DURATION_WARN}ms 的接口：
                {stats.abnormal.map((p) => PROVIDER_LABELS[p.provider]).join('、')}
              </span>
              <span>
                这些接口的异常会导致租户侧出现「外部校验暂不可用」，相关发票需人工核验接管。
                建议先查看「调用日志」中的超时与失败明细，再联系接口方排查。
              </span>
            </Space>
          }
        />
      ) : null}

      <PageState loading={metrics.loading} error={metrics.error} onRetry={metrics.reload} skeleton rows={8}>
        {data ? (
          <>
            <Row gutter={[16, 16]} style={{ marginBottom: 12 }}>
              <Col xs={12} lg={6}>
                <Card size="small">
                  <Statistic
                    title="租户总数"
                    value={data.tenantCount}
                    suffix="家"
                    prefix={<CloudServerOutlined />}
                  />
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    其中正常使用 {data.activeTenantCount} 家
                  </Typography.Text>
                </Card>
              </Col>
              <Col xs={12} lg={6}>
                <Card size="small">
                  <Statistic
                    title="活跃租户"
                    value={data.activeTenantCount}
                    suffix="家"
                    valueStyle={{ color: '#389e0d' }}
                    prefix={<RiseOutlined />}
                  />
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    活跃占比
                    {data.tenantCount > 0
                      ? ` ${Math.round((data.activeTenantCount / data.tenantCount) * 100)}%`
                      : ' -'}
                  </Typography.Text>
                </Card>
              </Col>
              <Col xs={12} lg={6}>
                <Card size="small">
                  <Statistic
                    title="本月发票处理量"
                    value={data.invoiceCountThisMonth}
                    suffix="张"
                    prefix={<FileTextOutlined />}
                  />
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    含全部状态的发票（含草稿与退回）
                  </Typography.Text>
                </Card>
              </Col>
              <Col xs={12} lg={6}>
                <Card size="small">
                  <Statistic
                    title="本月接口调用"
                    value={stats.totalCalls}
                    suffix="次"
                    prefix={<ApiOutlined />}
                  />
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    计费合计 ¥{(stats.totalCost / 100).toFixed(2)}
                  </Typography.Text>
                </Card>
              </Col>
            </Row>

            <Card size="small" title="外部接口健康度">
              <Table<ProviderStat>
                size="small"
                rowKey="provider"
                pagination={false}
                dataSource={data.providerStats}
                locale={{ emptyText: '暂无接口调用统计' }}
                columns={[
                  {
                    title: '接口',
                    dataIndex: 'provider',
                    width: 150,
                    render: (v: ProviderStat['provider']) => (
                      <Space direction="vertical" size={0}>
                        <span>{PROVIDER_LABELS[v]}</span>
                        <Typography.Text type="secondary" className="ilc-mono" style={{ fontSize: 11 }}>
                          {v}
                        </Typography.Text>
                      </Space>
                    ),
                  },
                  {
                    title: '调用次数',
                    dataIndex: 'totalCalls',
                    width: 120,
                    align: 'right',
                    sorter: (a, b) => a.totalCalls - b.totalCalls,
                    render: (v: number) => <span className="ilc-num">{v.toLocaleString('zh-CN')}</span>,
                  },
                  {
                    title: '成功次数',
                    dataIndex: 'successCalls',
                    width: 120,
                    align: 'right',
                    render: (v: number) => <span className="ilc-num">{v.toLocaleString('zh-CN')}</span>,
                  },
                  {
                    title: '成功率',
                    dataIndex: 'successRate',
                    width: 220,
                    sorter: (a, b) => a.successRate - b.successRate,
                    render: (v: number, record) => {
                      const percent = Math.round(v * 1000) / 10;
                      const color = rateColor(v);
                      return (
                        <Space direction="vertical" size={0} style={{ width: '100%' }}>
                          <Progress
                            percent={percent}
                            size="small"
                            strokeColor={color}
                            format={(p) => <span className="ilc-num">{p}%</span>}
                          />
                          <Typography.Text type="secondary" style={{ fontSize: 11, color }}>
                            {v < SUCCESS_RATE_BAD
                              ? '成功率偏低，需立即排查'
                              : v < SUCCESS_RATE_WARN
                                ? `低于健康线 ${Math.round(SUCCESS_RATE_WARN * 100)}%，请关注`
                                : '正常'}
                            {record.totalCalls - record.successCalls > 0
                              ? ` · 异常 ${record.totalCalls - record.successCalls} 次`
                              : ''}
                          </Typography.Text>
                        </Space>
                      );
                    },
                  },
                  {
                    title: '平均耗时',
                    dataIndex: 'avgDurationMs',
                    width: 130,
                    align: 'right',
                    sorter: (a, b) => a.avgDurationMs - b.avgDurationMs,
                    render: (v: number) => (
                      <Tooltip title={v >= DURATION_WARN ? '超过健康线，接口可能不稳定或网络抖动' : '正常范围'}>
                        <span className="ilc-num" style={{ color: durationColor(v) }}>
                          {v.toLocaleString('zh-CN')} ms
                        </span>
                      </Tooltip>
                    ),
                  },
                  {
                    title: '累计费用',
                    dataIndex: 'totalCostCents',
                    width: 130,
                    align: 'right',
                    sorter: (a, b) => a.totalCostCents - b.totalCostCents,
                    render: (v: number) => <span className="ilc-num">¥{(v / 100).toFixed(2)}</span>,
                  },
                  {
                    title: '判定',
                    key: 'judge',
                    width: 140,
                    render: (_v, record) =>
                      record.successRate >= SUCCESS_RATE_WARN && record.avgDurationMs < DURATION_WARN ? (
                        <Tag color="green">健康</Tag>
                      ) : record.successRate < SUCCESS_RATE_BAD || record.avgDurationMs >= DURATION_BAD ? (
                        <Tag color="red" icon={<WarningOutlined />}>
                          需排查
                        </Tag>
                      ) : (
                        <Tag color="orange">关注</Tag>
                      ),
                  },
                ]}
              />
            </Card>

            <Card size="small" title="指标口径说明" style={{ marginTop: 16 }}>
              <Space direction="vertical" size={6} style={{ width: '100%' }}>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  <strong>租户总数 / 活跃租户</strong>：活跃指状态为「正常」的租户；
                  待认证、已停用、已注销不计入活跃。
                </Typography.Text>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  <strong>本月发票处理量</strong>：按发票上传时间统计，包含草稿、校验中、待初审、已通过、已推送、
                  已退回、已归档的全部发票；重复上传的发票按去重后的实际单据计数。
                </Typography.Text>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  <strong>成功率</strong>：成功调用 / 总调用。被配额拦截、熔断、限流的调用计为失败——
                  它们虽然没有真正发出请求，但同样会导致校验不可用。
                </Typography.Text>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  <strong>平均耗时</strong>：仅统计真实发出的请求（沙箱与人工兜底不计入）。
                  验真与企查查通常在 1-2 秒，持续超过 3 秒说明接口方或网络存在问题。
                </Typography.Text>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  <strong>累计费用</strong>：按计费调用统计，沙箱、人工兜底与被拦截的调用不计费。
                </Typography.Text>
              </Space>
            </Card>
          </>
        ) : (
          <Alert type="warning" showIcon message="未取得运营指标数据" description="接口未返回数据，请点击刷新重试。" />
        )}
      </PageState>
    </div>
  );
}
